import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, ChevronLeft, ChevronRight, FileText, Heart, Layers, Rocket, Search, ShoppingCart, Tag, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MarketplaceOfferIcon } from '../real-gain/components/MarketplaceOfferNameCell';
import {
    CATEGORY_VALUES,
    PRICE_VALUES,
    SCOPE_VALUES,
    hasCollection,
    matchesPrice,
    matchesScope,
    parseCategory,
    readFilters,
    storeHref,
    writeFilters,
    type PriceFilter,
    type Scope,
} from './marketplaceFilters';
import { scrollToTop } from './scrollToTop';
import PageFade from './PageFade';

type StoreOffer = any;

/** Order of the category cards; the offer `_type` each one stands for. */
const TYPE_ORDER = CATEGORY_VALUES;

const PAGE_SIZE = 12;
const COLLECTION_COUNT = 4;
const FAVORITES_STORAGE_KEY = 'tri.marketplace.favorites';

const CARD_GRID_CLASS = 'grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-3';
const TOP_GRID_CLASS = 'grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-4';

/** Tinted type chip on the offer cards. */
const TYPE_CHIP_CLASS: Record<string, string> = {
    AgentToolset: 'bg-muted text-muted-foreground',
    KnowledgeBase: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
    AgenticWorkflow: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
    PromptTool: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
};

/**
 * Offer synopses are authored as markdown. The line clamp is applied by CSS
 * rather than by truncating the string, so it can never cut a `**` pair in half.
 */
function SynopsisMarkdown({ synopsis, className }: { synopsis?: string; className?: string }) {
    return (
        <div className={cn('min-w-0 text-sm leading-[1.45]', className)}>
            <Markdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <span>{children} </span> }}>
                {synopsis || ''}
            </Markdown>
        </div>
    );
}

function providerInitials(name?: string): string {
    const words = (name || '?').split(/\s+/).filter(Boolean);
    return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] || '?').slice(0, 2)).toUpperCase();
}

/** Stable hue per provider so the same provider always gets the same avatar color. */
function providerHue(name?: string): number {
    let hash = 0;
    const text = name || '';
    for (let index = 0; index < text.length; index += 1) {
        hash = (hash * 31 + text.charCodeAt(index)) % 360;
    }
    // 20 to 240 degrees (orange to blue): no pink or purple tones
    return 20 + (hash % 220);
}

function ProviderAvatar({ name, className }: { name?: string; className?: string }) {
    return (
        <Avatar className={cn('h-5 w-5 text-[10px]', className)}>
            <AvatarFallback
                className="font-semibold text-white"
                style={{ backgroundColor: `hsl(${providerHue(name)} 45% 42%)` }}
            >
                {providerInitials(name)}
            </AvatarFallback>
        </Avatar>
    );
}

/** Lowest monthly base fee across the plans, or `null` when the offer has no plans. */
function cheapestMonthlyFee(offer: StoreOffer): number | null {
    const fees = (offer.plans || [])
        .map((plan: any) => (typeof plan.baseFee === 'number' ? plan.baseFee : 0));
    return fees.length > 0 ? Math.min(...fees) : null;
}

function loadFavorites(): string[] {
    try {
        const parsed = JSON.parse(localStorage.getItem(FAVORITES_STORAGE_KEY) || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function saveFavorites(ids: string[]) {
    try {
        localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(ids));
    } catch {
        // Favorites are a per-browser convenience; without storage they just do not persist.
    }
}

/** "vor 2 Monaten" / "2 months ago" from an ISO date, in the UI language. */
function formatUpdatedAgo(iso: string | undefined, language: string): string | null {
    const time = iso ? new Date(iso).getTime() : NaN;
    if (Number.isNaN(time)) {
        return null;
    }
    const seconds = Math.round((time - Date.now()) / 1000);
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
        ['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60],
    ];
    const formatter = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
    for (const [unit, size] of units) {
        if (Math.abs(seconds) >= size) {
            return formatter.format(Math.round(seconds / size), unit);
        }
    }
    return formatter.format(0, 'minute');
}

/**
 * One chip group of the front page: every chip leads to the subpage of that filter. The
 * "all" chip is the front page itself and therefore always the current one here.
 */
function FilterChips<T extends string>({ label, values, allValue, getText, hrefFor }: {
    label: string;
    values: T[];
    allValue: T;
    getText: (value: T) => string;
    hrefFor: (value: T) => string;
}) {
    return (
        <nav className="flex min-w-0 flex-wrap items-center gap-2" aria-label={label}>
            <span className="w-14 shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
            {values.map((value) => {
                const current = value === allValue;
                return (
                    <Link
                        key={value}
                        to={hrefFor(value)}
                        aria-current={current ? 'page' : undefined}
                        className={cn(
                            'rounded-full border bg-card px-3 py-1 text-xs text-foreground transition-colors hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                            current && 'border-foreground bg-muted font-medium',
                        )}
                    >
                        {getText(value)}
                    </Link>
                );
            })}
        </nav>
    );
}

export interface MarketplaceStoreProps {
    offers: StoreOffer[];
    featuredOffers: StoreOffer[];
    loading: boolean;
    canOrder: (offer: StoreOffer) => boolean;
    /** Offers of the customer's own company: no order, just a note. */
    isOwned: (offer: StoreOffer) => boolean;
    onOpenDetails: (offer: StoreOffer) => void;
    onTest: (offer: StoreOffer) => void;
    onOrder: (offer: StoreOffer) => void;
}

export default function MarketplaceStore({
    offers,
    featuredOffers,
    loading,
    canOrder,
    isOwned,
    onOpenDetails,
    onTest,
    onOrder,
}: MarketplaceStoreProps) {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    // Scope, search text and price live in the URL (?scope=&q=&price=), as on the REAL GAIN site.
    const filters = readFilters(searchParams);
    const [query, setQuery] = useState(filters.q);
    const setFilters = (patch: Partial<typeof filters>) => setSearchParams(writeFilters(searchParams, patch), { replace: true });
    // Category and provider are URL state too: each filter value is a subpage of its own.
    const typeFilter = parseCategory(searchParams.get('category'));
    const providerFilter = searchParams.get('provider') || null;
    const inCollection = hasCollection(searchParams);
    const collectionKey = [providerFilter, typeFilter, filters.scope, filters.price].map((value) => value ?? '').join('|');
    const [page, setPageState] = useState(0);
    const rootRef = useRef<HTMLDivElement>(null);
    // Turning the page brings the list back to the top.
    const setPage = (next: number) => {
        setPageState(next);
        scrollToTop(rootRef.current);
    };
    const [favorites, setFavorites] = useState<string[]>(loadFavorites);

    const typeLabel = (type: string) => t(`agentPanel.store.types.${type}.label`, { defaultValue: type });

    const formatPrice = (offer: StoreOffer): string => {
        const fee = cheapestMonthlyFee(offer);
        if (fee === null) {
            return t('agentPanel.store.priceOnRequest');
        }
        if (fee === 0) {
            return t('agentPanel.store.priceFree');
        }
        const amount = new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: fee % 1 === 0 ? 0 : 2,
        }).format(fee);
        return t('agentPanel.store.priceFrom', { amount });
    };

    const typesPresent = useMemo(
        () => TYPE_ORDER.filter((type) => offers.some((offer) => offer._type === type)),
        [offers],
    );

    const providers = useMemo(() => {
        const byName = new Map<string, StoreOffer[]>();
        offers.forEach((offer) => {
            const name = offer.provider?.name;
            if (name) {
                byName.set(name, [...(byName.get(name) || []), offer]);
            }
        });
        return Array.from(byName.entries())
            .map(([name, providerOffers]) => ({ name, offers: providerOffers }))
            .sort((a, b) => b.offers.length - a.offers.length || a.name.localeCompare(b.name));
    }, [offers]);

    const normalizedQuery = query.trim().toLowerCase();
    const filtering = normalizedQuery !== '' || inCollection;

    const matching = useMemo(() => {
        const filtered = offers.filter((offer) => {
            if (typeFilter && offer._type !== typeFilter) {
                return false;
            }
            if (providerFilter && offer.provider?.name !== providerFilter) {
                return false;
            }
            if (!matchesScope(offer, filters.scope) || !matchesPrice(offer, filters.price)) {
                return false;
            }
            if (!normalizedQuery) {
                return true;
            }
            return [offer.name, offer.provider?.name, offer.synopsis, offer.description]
                .some((text) => typeof text === 'string' && text.toLowerCase().includes(normalizedQuery));
        });
        if (filtering || featuredOffers.length === 0) {
            return filtered;
        }
        // Unfiltered: the featured offers lead, the rest keeps its order.
        const featuredIds = new Set(featuredOffers.map((offer) => offer._id));
        return [
            ...filtered.filter((offer) => featuredIds.has(offer._id)),
            ...filtered.filter((offer) => !featuredIds.has(offer._id)),
        ];
    }, [offers, featuredOffers, typeFilter, providerFilter, filters.scope, filters.price, normalizedQuery, filtering]);

    const pageCount = Math.max(1, Math.ceil(matching.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount - 1);
    const pageOffers = matching.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

    // A new filter starts at the first page.
    useEffect(() => {
        setPageState(0);
    }, [query, typeFilter, providerFilter, filters.scope, filters.price]);

    // Field to URL, slightly delayed: the panel re-renders on every URL change.
    useEffect(() => {
        if (query === filters.q) {
            return undefined;
        }
        const timer = window.setTimeout(() => setFilters({ q: query }), 250);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    // Back to the front page, without any filter.
    const resetFilters = () => {
        setQuery('');
        setSearchParams(new URLSearchParams({ panel: 'agent', tab: 'marketplace' }));
    };

    const removeFilter = (key: string) => {
        const next = new URLSearchParams(searchParams);
        next.delete(key);
        setSearchParams(next);
    };

    const toggleFavorite = (id: string) => {
        setFavorites((current) => {
            const next = current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id];
            saveFavorites(next);
            return next;
        });
    };

    const renderCard = (offer: StoreOffer) => {
        const open = () => onOpenDetails(offer);
        const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                open();
            }
        };
        const isFavorite = favorites.includes(offer._id);
        const updatedAgo = formatUpdatedAgo(offer.updated, i18n.language);
        const planCount = (offer.plans || []).length;
        const fileCount = (offer.files || offer.managedFiles || []).length;
        return (
            <div
                key={offer._id}
                role="button"
                tabIndex={0}
                aria-label={`${t('settingsPanel.agentPanel.showDetails')}: ${offer.name}`}
                onClick={open}
                onKeyDown={handleKeyDown}
                className="group flex min-w-0 cursor-pointer flex-col gap-3 rounded-lg border bg-card p-4 text-card-foreground shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
                <div className="flex items-center justify-between gap-2">
                    <span className={cn('rounded px-2 py-0.5 text-xs font-medium', TYPE_CHIP_CLASS[offer._type] || TYPE_CHIP_CLASS.AgentToolset)}>
                        {typeLabel(offer._type)}
                    </span>
                    <button
                        type="button"
                        aria-pressed={isFavorite}
                        aria-label={`${isFavorite ? t('agentPanel.store.removeFavorite') : t('agentPanel.store.addFavorite')}: ${offer.name}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            toggleFavorite(offer._id);
                        }}
                        className="rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        <Heart aria-hidden className={cn('h-4 w-4', isFavorite && 'fill-destructive text-destructive')} />
                    </button>
                </div>
                <div className="flex items-start gap-3">
                    <MarketplaceOfferIcon
                        neutral
                        offer={offer}
                        className="h-10 w-10 shrink-0 rounded-full border bg-background p-2"
                    />
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold" title={offer.name}>{offer.name}</div>
                        <SynopsisMarkdown synopsis={offer.synopsis} className="mt-0.5 line-clamp-2 min-h-[2.9em] text-xs text-muted-foreground" />
                        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                            <span className="min-w-0 truncate">{t('agentPanel.store.by', { name: offer.provider?.name })}</span>
                            {updatedAgo && <span className="shrink-0">{t('agentPanel.store.updated', { when: updatedAgo })}</span>}
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs">
                        <span className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5">
                            <Tag aria-hidden className="h-3 w-3 text-muted-foreground" />
                            {formatPrice(offer)}
                        </span>
                        {planCount > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5">
                                <Layers aria-hidden className="h-3 w-3 text-muted-foreground" />
                                {t('agentPanel.store.planCount', { count: planCount })}
                            </span>
                        )}
                        {isOwned(offer) && (
                            <span className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5">
                                <Check aria-hidden className="h-3 w-3 text-muted-foreground" />
                                {t('agentPanel.store.owned')}
                            </span>
                        )}
                        {fileCount > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5">
                                <FileText aria-hidden className="h-3 w-3 text-muted-foreground" />
                                {t('agentPanel.store.documentCount', { count: fileCount })}
                            </span>
                        )}
                    </div>
                    <div className="flex shrink-0 items-center opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-label={`${t('settingsPanel.agentPanel.test')}: ${offer.name}`}
                            title={t('settingsPanel.agentPanel.test')}
                            onClick={(event) => {
                                event.stopPropagation();
                                onTest(offer);
                            }}
                        >
                            <Rocket aria-hidden className="h-4 w-4" />
                        </Button>
                        {canOrder(offer) && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                aria-label={`${t('settingsPanel.agentPanel.order')}: ${offer.name}`}
                                title={t('settingsPanel.agentPanel.order')}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onOrder(offer);
                                }}
                            >
                                <ShoppingCart aria-hidden className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const sectionHeading = (title: string, action?: { label: string; onClick: () => void }) => (
        <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-xl font-semibold tracking-tight">{title}</h3>
            {action && (
                <button
                    type="button"
                    onClick={action.onClick}
                    className="rounded-sm text-xs font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    {action.label}
                </button>
            )}
        </div>
    );

    /** Placeholder for the category and provider cards, which only exist once the offers are in. */
    const renderSkeletonTopCards = (count: number) => (
        <div className={TOP_GRID_CLASS} aria-hidden>
            {Array.from({ length: count }, (_, index) => (
                <div key={index} className="flex flex-col gap-2 rounded-lg border bg-card p-4">
                    <Skeleton className="mb-1 h-6 w-6" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-4/5" />
                    <Skeleton className="mt-1 h-3 w-1/3" />
                </div>
            ))}
        </div>
    );

    const renderSkeletonCards = () => (
        <div className={CARD_GRID_CLASS}>
            {Array.from({ length: 6 }, (_, index) => (
                <div key={index} className="flex flex-col gap-3 rounded-lg border bg-card p-4">
                    <Skeleton className="h-4 w-16" />
                    <div className="flex items-start gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="flex flex-1 flex-col gap-2">
                            <Skeleton className="h-4 w-2/3" />
                            <Skeleton className="h-3 w-full" />
                            <Skeleton className="h-3 w-5/6" />
                        </div>
                    </div>
                    <Skeleton className="h-5 w-1/2" />
                </div>
            ))}
        </div>
    );

    const renderOffers = () => {
        if (loading) {
            return renderSkeletonCards();
        }
        if (offers.length === 0) {
            return (
                <p className="max-w-prose py-8 text-sm text-muted-foreground">
                    {t('settingsPanel.agentPanel.noAgentsHint')}
                </p>
            );
        }
        if (matching.length === 0) {
            return (
                <div className="flex flex-col items-center gap-2 py-12 text-center">
                    <div className="text-sm font-semibold">{t('settingsPanel.agentPanel.noMatchingAgentsTitle')}</div>
                    <div className="max-w-prose text-sm text-muted-foreground">
                        {t('settingsPanel.agentPanel.noMatchingAgentsHint')}
                    </div>
                    <Button variant="outline" size="sm" onClick={resetFilters}>{t('global.clear')}</Button>
                </div>
            );
        }
        return (
            <>
                <div className={CARD_GRID_CLASS}>{pageOffers.map(renderCard)}</div>
                {pageCount > 1 && (
                    <nav className="flex items-center justify-between pt-2 text-xs" aria-label={t('agentPanel.store.pagination')}>
                        <button
                            type="button"
                            disabled={currentPage === 0}
                            onClick={() => setPage(currentPage - 1)}
                            className="inline-flex items-center gap-1 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
                        >
                            <ChevronLeft aria-hidden className="h-3.5 w-3.5" />
                            {t('agentPanel.store.previousPage')}
                        </button>
                        <span className="text-muted-foreground">
                            {t('agentPanel.store.pageOf', { page: currentPage + 1, total: pageCount })}
                        </span>
                        <button
                            type="button"
                            disabled={currentPage >= pageCount - 1}
                            onClick={() => setPage(currentPage + 1)}
                            className="inline-flex items-center gap-1 rounded-sm font-medium hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
                        >
                            {t('agentPanel.store.nextPage')}
                            <ChevronRight aria-hidden className="h-3.5 w-3.5" />
                        </button>
                    </nav>
                )}
            </>
        );
    };

    const offersHeading = filtering
        ? t('agentPanel.store.results', { count: matching.length })
        : t('agentPanel.store.allOffers');

    const searchField = (
        <div className="relative w-full max-w-xl">
            <Search aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('agentPanel.store.searchPlaceholder')}
                aria-label={t('agentPanel.store.searchPlaceholder')}
                className="h-10 w-full rounded-lg border bg-card pl-10 pr-10 text-sm shadow-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-search-cancel-button]:hidden"
            />
            {query && (
                <button
                    type="button"
                    aria-label={t('global.clear')}
                    onClick={() => {
                        setQuery('');
                        setFilters({ q: '' });
                    }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    <X aria-hidden className="h-4 w-4" />
                </button>
            )}
        </div>
    );

    // ---- Filter subpage: only the offers of one filter, with its own header and breadcrumb.
    if (inCollection) {
        const active = [
            providerFilter ? { key: 'provider', title: providerFilter, text: providerFilter } : null,
            typeFilter ? { key: 'category', title: typeLabel(typeFilter), text: typeLabel(typeFilter) } : null,
            filters.scope !== 'all'
                ? { key: 'scope', title: t(`agentPanel.store.filters.title.${filters.scope}`), text: t(`agentPanel.store.filters.scope.${filters.scope}`) }
                : null,
            filters.price !== 'all'
                ? { key: 'price', title: t(`agentPanel.store.filters.priceTitle.${filters.price}`), text: t(`agentPanel.store.filters.price.${filters.price}`) }
                : null,
        ].filter((entry): entry is NonNullable<typeof entry> => entry !== null);
        const [primary, ...others] = active;
        let description = '';
        if (primary.key === 'provider') {
            description = t('agentPanel.store.filters.providerDescription', { count: matching.length, name: primary.text });
        } else if (primary.key === 'category' && typeFilter) {
            description = t(`agentPanel.store.types.${typeFilter}.description`);
        } else if (primary.key === 'scope') {
            description = t(`agentPanel.store.filters.scopeDescription.${filters.scope}`);
        } else {
            description = t(`agentPanel.store.filters.priceDescription.${filters.price}`);
        }

        return (
            <div ref={rootRef} className="flex min-w-0 flex-col gap-6">
                <header className="flex flex-col gap-4 rounded-xl bg-muted/60 px-6 py-8 sm:px-10">
                    <div className="flex min-w-0 items-center gap-3">
                        {primary.key === 'provider' && <ProviderAvatar name={primary.text} className="h-10 w-10 text-sm" />}
                        <h2 className="min-w-0 break-words text-2xl font-bold tracking-tight">{primary.title}</h2>
                    </div>
                    <p className="max-w-xl text-sm text-muted-foreground">{description}</p>
                    {searchField}
                    {others.length > 0 && (
                        <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs">
                            <span className="font-medium text-muted-foreground">{t('agentPanel.store.filters.alsoFiltered')}</span>
                            {others.map((entry) => (
                                <button
                                    key={entry.key}
                                    type="button"
                                    onClick={() => removeFilter(entry.key)}
                                    className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                >
                                    {entry.text}
                                    <X aria-hidden className="h-3 w-3" />
                                </button>
                            ))}
                        </div>
                    )}
                </header>

                <PageFade fadeKey={`collection:${collectionKey}`}>
                    <section className="flex min-w-0 flex-col gap-4" aria-label={offersHeading}>
                        {sectionHeading(offersHeading)}
                        {renderOffers()}
                    </section>
                </PageFade>
            </div>
        );
    }

    // ---- Front page
    return (
        <div ref={rootRef} className="flex min-w-0 flex-col gap-10">
            <header className="flex flex-col gap-4 rounded-xl bg-muted/60 px-6 py-8 sm:px-10">
                <h2 className="text-2xl font-bold tracking-tight">{t('agentPanel.store.title')}</h2>
                <p className="max-w-xl text-sm text-muted-foreground">{t('agentPanel.store.subtitle')}</p>
                {searchField}
                <div className="flex min-w-0 flex-col gap-2">
                    <FilterChips<Scope>
                        label={t('agentPanel.store.filters.scopeLabel')}
                        values={SCOPE_VALUES}
                        allValue="all"
                        getText={(value) => t(`agentPanel.store.filters.scope.${value}`)}
                        hrefFor={(value) => (value === 'all' ? storeHref() : storeHref({ scope: value }))}
                    />
                    <FilterChips<PriceFilter>
                        label={t('agentPanel.store.filters.priceLabel')}
                        values={PRICE_VALUES}
                        allValue="all"
                        getText={(value) => t(`agentPanel.store.filters.price.${value}`)}
                        hrefFor={(value) => (value === 'all' ? storeHref() : storeHref({ price: value }))}
                    />
                </div>
            </header>

            <PageFade fadeKey="front" className="flex min-w-0 flex-col gap-10">
                {loading && (
                    <section className="flex min-w-0 flex-col gap-4" aria-busy="true" aria-label={t('agentPanel.store.topCategories')}>
                        {sectionHeading(t('agentPanel.store.topCategories'))}
                        {renderSkeletonTopCards(3)}
                    </section>
                )}
                {!loading && typesPresent.length > 0 && (
                    <section className="flex min-w-0 flex-col gap-4" aria-label={t('agentPanel.store.topCategories')}>
                        {sectionHeading(t('agentPanel.store.topCategories'))}
                        <div className={TOP_GRID_CLASS}>
                            {typesPresent.map((type) => (
                                <Link
                                    key={type}
                                    to={storeHref({ category: type })}
                                    className="flex min-w-0 flex-col items-start gap-1.5 rounded-lg border bg-card p-4 text-left text-foreground shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                >
                                    <MarketplaceOfferIcon neutral offer={{ _type: type }} className="mb-1 h-6 w-6" />
                                    <span className="text-sm font-semibold">{typeLabel(type)}</span>
                                    <span className="line-clamp-3 text-xs text-muted-foreground">
                                        {t(`agentPanel.store.types.${type}.description`)}
                                    </span>
                                    <span className="mt-1 text-xs font-medium text-foreground">
                                        {t('agentPanel.store.browseCategory')}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                <section className="flex min-w-0 flex-col gap-4" aria-label={offersHeading}>
                    {sectionHeading(offersHeading)}
                    {renderOffers()}
                </section>

                {loading && (
                    <section className="flex min-w-0 flex-col gap-4" aria-busy="true" aria-label={t('agentPanel.store.topCollections')}>
                        {sectionHeading(t('agentPanel.store.topCollections'))}
                        {renderSkeletonTopCards(4)}
                    </section>
                )}
                {!loading && providers.length > 1 && (
                    <section className="flex min-w-0 flex-col gap-4" aria-label={t('agentPanel.store.topCollections')}>
                        {sectionHeading(t('agentPanel.store.topCollections'))}
                        <div className={TOP_GRID_CLASS}>
                            {providers.slice(0, COLLECTION_COUNT).map((provider) => (
                                <div key={provider.name} className="flex min-w-0 flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm">
                                    <div className="min-w-0">
                                        <div className="truncate text-sm font-semibold" title={provider.name}>{provider.name}</div>
                                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                                            {t('agentPanel.store.collectionDescription', {
                                                count: provider.offers.length,
                                                names: provider.offers.slice(0, 2).map((offer: StoreOffer) => offer.name).join(', '),
                                            })}
                                        </p>
                                    </div>
                                    <Link
                                        to={storeHref({ provider: provider.name })}
                                        className="mt-auto flex items-center gap-2 rounded-sm text-left text-xs font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    >
                                        <ProviderAvatar name={provider.name} className="h-8 w-8 text-xs" />
                                        {t('agentPanel.store.browseCollection')}
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </PageFade>
        </div>
    );
}
